/**
 * O minimapa do escritório, à GTA: um disco no canto com o mundo à nossa volta,
 * e um mapa grande (tecla M, ou um clique no disco) onde se carrega num sítio
 * para o boneco ir até lá sozinho.
 *
 * Não é um segundo motor de nada. Lê o que já existe — a posição do boneco
 * (`posRef`, escrita pelo loop do Phaser), a das outras pessoas, as chamadas de
 * voz e as trancas das salas (a store) e o trajecto automático em curso
 * (`rotaRef`) — e para andar usa o mesmo piloto automático do "ir ter com
 * alguém": o mesmo A*, as mesmas paredes, as mesmas portas trancadas.
 *
 * Desenha num canvas próprio, a cada frame, sem re-renderizar o React: o estado
 * React é só o texto por baixo do disco, e esse só muda quando muda de facto.
 */
import {
  COLS,
  type Facing,
  MAP_WIDTH,
  placeName,
  ROOM_LABELS,
  ROWS,
  type Room,
  type Snowflake,
  TILE,
} from "@greenvoice/shared";
import { type MouseEvent, type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { useStore } from "../store/store";

import { IconClose } from "./Icons";
import { LERP, SALTO_REMOTO_PX } from "./officeAvatars";
import { NOME_DO_TEMPO, SIMBOLO_DO_TEMPO, type Tempo, tempoAgora, tempoEscolhido } from "./officeClima";
import {
  anguloDoRumo,
  aproximarPosicoes,
  type Banda,
  bandaDe,
  destinoMaisProximo,
  horaDoRelogio,
  limitesDaBanda,
  naBorda,
  PONTOS_DE_INTERESSE,
  type PontoDeInteresse,
  pessoasPorSala,
  pessoasVisiveis,
  pintarFundo,
  salasTrancadas,
  type TipoDePonto,
} from "./officeMap";

/** O trajecto automático em curso: os pontos e o índice do próximo. */
export interface Rota {
  path: readonly { x: number; y: number }[];
  idx: number;
}

interface Pessoa {
  id: Snowflake;
  nome: string;
  x: number;
  y: number;
  emChamada: boolean;
}

interface Cena {
  eu: { x: number; y: number; facing: Facing };
  banda: Banda;
  /** A banda onde o boneco está — o mapa grande pode estar a mostrar outra. */
  bandaDoEu: Banda;
  pessoas: Pessoa[];
  trancadas: Room[];
  porSala: Map<string, number>;
  rota: Rota | null;
  destino: { x: number; y: number } | null;
}

/** Quantos tiles se vêem do centro à borda do disco. */
const RAIO_EM_TILES = 11;
/** Resolução do mapa pintado: pixels de imagem por tile. */
const PX_POR_TILE = 16;
/** Até que distância (tiles) o disco escreve o nome de quem está perto. */
const NOMES_ATE = 7;

const COR_PESSOA = "#60a5fa";
const COR_EM_CHAMADA = "#4ade80";
const COR_DESTINO = "#facc15";
const COR_TRANCADA = "#ef4444";

export const COR_DO_PONTO: Record<TipoDePonto, string> = {
  ponto: "#b45309",
  quadro: "#0284c7",
  jogo: "#7c3aed",
  arcade: "#db2777",
  tv: "#475569",
  escotilha: "#d97706",
};

let fundoEmCache: HTMLCanvasElement | null | undefined;

/** O mapa inteiro pintado uma vez e reutilizado pelos dois canvas. */
function fundo(): HTMLCanvasElement | null {
  if (fundoEmCache !== undefined) return fundoEmCache;
  const canvas = document.createElement("canvas");
  canvas.width = COLS * PX_POR_TILE;
  canvas.height = ROWS * PX_POR_TILE;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    fundoEmCache = null;
    return null;
  }
  pintarFundo(ctx, PX_POR_TILE);
  fundoEmCache = canvas;
  return canvas;
}

/** Onde fica o mundo no ecrã: o ponto do mundo no centro (`ox`,`oy`) e a escala. */
function vistaDe(w: number, h: number, cena: Cena, redondo: boolean): { ox: number; oy: number; k: number } {
  if (redondo) return { ox: cena.eu.x, oy: cena.eu.y, k: w / 2 / (RAIO_EM_TILES * TILE) };
  const { y0, y1 } = limitesDaBanda(cena.banda);
  const folga = 24;
  const k = Math.min((w - folga) / MAP_WIDTH, (h - folga) / (y1 - y0));
  return { ox: MAP_WIDTH / 2, oy: (y0 + y1) / 2, k };
}

/** Texto com contorno escuro — legível sobre qualquer chão. */
function escrever(ctx: CanvasRenderingContext2D, texto: string, x: number, y: number, fonte: string, cor = "#ffffff") {
  ctx.font = fonte;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
  ctx.strokeText(texto, x, y);
  ctx.fillStyle = cor;
  ctx.fillText(texto, x, y);
}

/** O ícone de um ponto de interesse: um selo redondo com o desenho a branco. */
function desenharIcone(ctx: CanvasRenderingContext2D, ponto: PontoDeInteresse, x: number, y: number, r: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = COR_DO_PONTO[ponto.tipo];
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "#0b0f13";
  ctx.stroke();

  const u = r * 0.55;
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#ffffff";
  ctx.lineWidth = Math.max(1.2, r / 5);
  ctx.lineCap = "round";
  ctx.beginPath();
  switch (ponto.tipo) {
    case "ponto": // um relógio
      ctx.arc(0, 0, u, 0, Math.PI * 2);
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -u * 0.65);
      ctx.moveTo(0, 0);
      ctx.lineTo(u * 0.5, 0);
      ctx.stroke();
      break;
    case "quadro": // uma tela com um traço
      ctx.rect(-u, -u * 0.7, u * 2, u * 1.4);
      ctx.moveTo(-u * 0.5, u * 0.15);
      ctx.lineTo(u * 0.5, -u * 0.25);
      ctx.stroke();
      break;
    case "jogo": // quatro peças
      for (const [dx, dy] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ]) {
        ctx.moveTo((dx ?? 0) * u * 0.45 + r * 0.16, (dy ?? 0) * u * 0.45);
        ctx.arc((dx ?? 0) * u * 0.45, (dy ?? 0) * u * 0.45, r * 0.16, 0, Math.PI * 2);
      }
      ctx.fill();
      break;
    case "arcade": // um manípulo
      ctx.moveTo(0, u * 0.7);
      ctx.lineTo(0, -u * 0.2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, -u * 0.45, r * 0.24, 0, Math.PI * 2);
      ctx.fill();
      break;
    case "tv": // um ecrã com pé
      ctx.rect(-u, -u * 0.65, u * 2, u * 1.15);
      ctx.moveTo(-u * 0.45, u * 0.85);
      ctx.lineTo(u * 0.45, u * 0.85);
      ctx.stroke();
      break;
    case "escotilha": {
      // uma seta para onde se vai
      const s = ponto.seta === "cima" ? -1 : 1;
      ctx.moveTo(0, -u * 0.75 * s);
      ctx.lineTo(0, u * 0.75 * s);
      ctx.moveTo(-u * 0.55, u * 0.2 * s);
      ctx.lineTo(0, u * 0.75 * s);
      ctx.lineTo(u * 0.55, u * 0.2 * s);
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

/** Um cadeado, para as salas trancadas. */
function desenharCadeado(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = COR_TRANCADA;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "#0b0f13";
  ctx.stroke();
  const u = r * 0.5;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(-u, -u * 0.1, u * 2, u * 1.2);
  ctx.beginPath();
  ctx.arc(0, -u * 0.1, u * 0.65, Math.PI, 0);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(1.2, r / 5);
  ctx.stroke();
  ctx.restore();
}

function desenhar(canvas: HTMLCanvasElement | null, cena: Cena, redondo: boolean): void {
  if (!canvas) return;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (w === 0 || h === 0) return;
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { ox, oy, k } = vistaDe(w, h, cena, redondo);
  const X = (wx: number) => w / 2 + (wx - ox) * k;
  const Y = (wy: number) => h / 2 + (wy - oy) * k;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  if (redondo) {
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
    ctx.clip();
  }
  ctx.fillStyle = "#0b0f13";
  ctx.fillRect(0, 0, w, h);

  // ── O mapa pintado (chão, móveis, árvores), só da banda onde se está.
  const { y0, y1 } = limitesDaBanda(cena.banda);
  const imagem = fundo();
  if (imagem) {
    const escala = TILE / PX_POR_TILE;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(imagem, 0, y0 / escala, imagem.width, (y1 - y0) / escala, X(0), Y(y0), MAP_WIDTH * k, (y1 - y0) * k);
  }

  // ── Salas trancadas: vermelho por cima e um cadeado ao meio.
  for (const sala of cena.trancadas) {
    if (bandaDe((sala.row + sala.h / 2) * TILE) !== cena.banda) continue;
    const sx = X(sala.col * TILE);
    const sy = Y(sala.row * TILE);
    const sw = sala.w * TILE * k;
    const sh = sala.h * TILE * k;
    ctx.fillStyle = "rgba(239, 68, 68, 0.2)";
    ctx.fillRect(sx, sy, sw, sh);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(239, 68, 68, 0.85)";
    ctx.strokeRect(sx, sy, sw, sh);
    desenharCadeado(ctx, sx + sw / 2, sy + sh / 2, redondo ? 6 : 9);
  }

  // ── Os pontos de interesse. ANTES dos nomes das salas: um ícone encostado à
  // porta (a arcade da Sala de Descanso) tapava o início do nome.
  for (const ponto of PONTOS_DE_INTERESSE) {
    if (bandaDe(ponto.y) !== cena.banda) continue;
    desenharIcone(ctx, ponto, X(ponto.x), Y(ponto.y), redondo ? 6 : 9);
  }

  // ── Os nomes das salas (e, no mapa grande, quantas pessoas estão lá).
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Num disco pequeno (o do telemóvel) os nomes das salas atropelam-se: fica o desenho.
  const comNomes = !redondo || w >= 130;
  for (const l of ROOM_LABELS) {
    if (!comNomes) break;
    if (bandaDe(l.y) !== cena.banda) continue;
    const n = cena.porSala.get(l.roomId) ?? 0;
    const texto = !redondo && n > 0 ? `${l.text} · ${n}` : l.text;
    escrever(
      ctx,
      texto,
      X(l.x),
      Y(l.y),
      redondo ? "700 9px Inter, system-ui, sans-serif" : "600 12px Inter, system-ui, sans-serif",
    );
  }

  // O boneco, a rota e o cone só se desenham no andar onde ele está.
  const euAqui = cena.bandaDoEu === cena.banda;

  // ── A rota do piloto automático, como o GPS do GTA.
  if (cena.rota && euAqui) {
    const pontos = [cena.eu, ...cena.rota.path.slice(cena.rota.idx)].filter((p) => bandaDe(p.y) === cena.banda);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    for (const [cor, largura] of [
      ["rgba(0, 0, 0, 0.55)", redondo ? 5 : 6],
      [COR_DESTINO, redondo ? 3 : 3.5],
    ] as const) {
      ctx.beginPath();
      for (const [i, p] of pontos.entries()) {
        if (i === 0) ctx.moveTo(X(p.x), Y(p.y));
        else ctx.lineTo(X(p.x), Y(p.y));
      }
      ctx.strokeStyle = cor;
      ctx.lineWidth = largura;
      ctx.stroke();
    }
  }

  // ── O destino.
  if (cena.destino && bandaDe(cena.destino.y) === cena.banda) {
    let dx = X(cena.destino.x) - w / 2;
    let dy = Y(cena.destino.y) - h / 2;
    if (redondo) ({ dx, dy } = naBorda(dx, dy, w / 2 - 8));
    ctx.beginPath();
    ctx.arc(w / 2 + dx, h / 2 + dy, redondo ? 5 : 7, 0, Math.PI * 2);
    ctx.fillStyle = COR_DESTINO;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0b0f13";
    ctx.stroke();
  }

  // ── O cone de visão: para onde o boneco está virado.
  if (euAqui) {
    ctx.save();
    ctx.translate(X(cena.eu.x), Y(cena.eu.y));
    ctx.rotate(anguloDoRumo(cena.eu.facing));
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, (redondo ? 3.2 : 2.5) * TILE * k, -0.55, 0.55);
    ctx.closePath();
    const cone = ctx.createRadialGradient(0, 0, 0, 0, 0, (redondo ? 3.2 : 2.5) * TILE * k);
    cone.addColorStop(0, "rgba(255, 255, 255, 0.35)");
    cone.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = cone;
    ctx.fill();
    ctx.restore();
  }

  // ── As outras pessoas. A verde quem está numa chamada; no disco, quem não cabe fica na borda.
  for (const p of cena.pessoas) {
    let dx = X(p.x) - w / 2;
    let dy = Y(p.y) - h / 2;
    let fora = false;
    if (redondo) ({ dx, dy, fora } = naBorda(dx, dy, w / 2 - 7));
    const px = w / 2 + dx;
    const py = h / 2 + dy;
    ctx.beginPath();
    ctx.arc(px, py, redondo ? (fora ? 3.5 : 4.5) : 6, 0, Math.PI * 2);
    ctx.fillStyle = p.emChamada ? COR_EM_CHAMADA : COR_PESSOA;
    ctx.globalAlpha = fora ? 0.8 : 1;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();

    const perto = Math.hypot(p.x - cena.eu.x, p.y - cena.eu.y) <= NOMES_ATE * TILE;
    if (!redondo || (perto && !fora)) {
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      escrever(
        ctx,
        p.nome,
        px,
        py - (redondo ? 6 : 9),
        redondo ? "600 9px Inter, system-ui, sans-serif" : "600 11px Inter, system-ui, sans-serif",
      );
    }
  }

  // ── Eu: uma seta branca virada para onde o boneco olha.
  if (euAqui) {
    ctx.save();
    ctx.translate(X(cena.eu.x), Y(cena.eu.y));
    ctx.rotate(anguloDoRumo(cena.eu.facing));
    const s = redondo ? 7 : 8;
    ctx.beginPath();
    ctx.moveTo(s, 0);
    ctx.lineTo(-s * 0.7, s * 0.65);
    ctx.lineTo(-s * 0.35, 0);
    ctx.lineTo(-s * 0.7, -s * 0.65);
    ctx.closePath();
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0b0f13";
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();
}

/** Os andares, de cima para baixo — é a ordem dos botões do mapa grande. */
const ANDARES: readonly Banda[] = ["andar1", "superficie", "subsolo"];
export const NOME_DA_BANDA: Record<Banda, string> = {
  andar1: "1.º andar",
  superficie: "Superfície",
  subsolo: "Bunker",
};

interface Props {
  serverId: Snowflake;
  posRef: MutableRefObject<{ x: number; y: number; facing: Facing }>;
  rotaRef: MutableRefObject<Rota | null>;
  aberto: boolean;
  /** Outra caixa tem o teclado (balão, quadro, ponto…): o M não abre o mapa. */
  ocupado: boolean;
  onAbrir: (aberto: boolean) => void;
  /** Leva o boneco a um ponto. `false` = não há caminho até lá. */
  onIrPara: (x: number, y: number) => boolean;
  onIrTerCom: (userId: Snowflake) => void;
}

export function OfficeMinimap({ serverId, posRef, rotaRef, aberto, ocupado, onAbrir, onIrPara, onIrTerCom }: Props) {
  const discoRef = useRef<HTMLCanvasElement>(null);
  const grandeRef = useRef<HTMLCanvasElement>(null);
  const destinoRef = useRef<{ x: number; y: number } | null>(null);
  const [lugar, setLugar] = useState(() => placeName(posRef.current.x, posRef.current.y));
  const [banda, setBanda] = useState<Banda>(() => bandaDe(posRef.current.y));
  const [companhia, setCompanhia] = useState(0);
  const [hora, setHora] = useState(() => horaDoRelogio(new Date()));
  const [tempo, setTempo] = useState<Tempo>(() => tempoAgora());
  const [escolhido, setEscolhido] = useState(() => tempoEscolhido() !== null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  // O andar que o mapa grande mostra; `null` = o andar onde se está. O ciclo de
  // desenho lê-o pela ref, sem se refazer a cada troca.
  const [vista, setVista] = useState<Banda | null>(null);
  const vistaRef = useRef<Banda | null>(null);
  vistaRef.current = vista;

  // Onde cada pessoa está DESENHADA — vai atrás da posição do servidor (ver
  // `aproximarPosicoes`). Actualiza-se uma vez por frame, no ciclo abaixo.
  const mostradasRef = useRef(new Map<string, { x: number; y: number }>());

  const cena = useCallback(
    (mostrar: Banda | null = null): Cena => {
      const eu = posRef.current;
      const estado = useStore.getState();
      const rota = rotaRef.current;
      // Sem rota, não há destino: chegou, desistiu, ou mexeu-se nas teclas.
      if (!rota) destinoRef.current = null;
      const bandaDoEu = bandaDe(eu.y);
      const banda = mostrar ?? bandaDoEu;
      const pessoas = pessoasVisiveis(
        Object.values(estado.positions[serverId] ?? {}),
        estado.self?.id ?? null,
        limitesDaBanda(banda).y0,
      ).map((o): Pessoa => {
        const u = estado.users[o.userId];
        const aqui = mostradasRef.current.get(o.userId) ?? o;
        return {
          id: o.userId,
          nome: u ? (u.displayName ?? u.username) : "alguém",
          x: aqui.x,
          y: aqui.y,
          // Numa call, ou em ligação privada COMIGO: quem está do outro lado da
          // linha fica verde no mapa, para se saber onde anda.
          emChamada:
            estado.voiceStates[o.userId] !== undefined ||
            (estado.ligacao?.estado === "em_curso" && estado.ligacao.outro === o.userId),
        };
      });
      return {
        eu,
        banda,
        bandaDoEu,
        pessoas,
        trancadas: salasTrancadas(estado.roomLocks[serverId]),
        porSala: pessoasPorSala([eu, ...pessoas]),
        rota,
        destino: destinoRef.current,
      };
    },
    [serverId, posRef, rotaRef],
  );

  useEffect(() => {
    let pedido = 0;
    let ultimoDesenho = 0;
    let ultimoTexto = 0;
    const passo = (t: number) => {
      pedido = requestAnimationFrame(passo);
      const dt = ultimoDesenho === 0 ? 0 : t - ultimoDesenho;
      ultimoDesenho = t;
      // A 30fps a bolinha de quem anda ainda se via aos solavancos: desenha-se a
      // cada frame, que o disco é pequeno e o mapa grande só custa quando aberto.
      const ocupantes = Object.values(useStore.getState().positions[serverId] ?? {});
      aproximarPosicoes(mostradasRef.current, ocupantes, dt, LERP, SALTO_REMOTO_PX);
      const agora = cena();
      desenhar(discoRef.current, agora, true);
      desenhar(grandeRef.current, vistaRef.current ? cena(vistaRef.current) : agora, false);
      if (t - ultimoTexto > 250) {
        ultimoTexto = t;
        const aqui = placeName(agora.eu.x, agora.eu.y);
        setLugar(aqui);
        setBanda(agora.banda);
        setCompanhia(agora.pessoas.filter((p) => placeName(p.x, p.y) === aqui).length);
        setHora(horaDoRelogio(new Date()));
        setTempo(tempoAgora());
        setEscolhido(tempoEscolhido() !== null);
      }
    };
    pedido = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(pedido);
  }, [cena, serverId]);

  // M abre e fecha o mapa; Escape fecha. Nunca enquanto se escreve.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const alvo = e.target;
      if (
        alvo instanceof HTMLElement &&
        (alvo.tagName === "INPUT" || alvo.tagName === "TEXTAREA" || alvo.isContentEditable)
      ) {
        return;
      }
      if (e.key === "Escape" && aberto) {
        onAbrir(false);
        return;
      }
      if ((e.key === "m" || e.key === "M") && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!aberto && ocupado) return;
        onAbrir(!aberto);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto, ocupado, onAbrir]);

  // Fechar o mapa volta a vista para o andar onde se está.
  useEffect(() => {
    if (!aberto) setVista(null);
  }, [aberto]);

  useEffect(() => {
    if (!aviso) return;
    const t = window.setTimeout(() => setAviso(null), 2500);
    return () => window.clearTimeout(t);
  }, [aviso]);

  /** Do ponto do rato no mapa grande para o ponto do mundo. */
  const noMundo = (e: MouseEvent<HTMLCanvasElement>) => {
    const caixa = e.currentTarget.getBoundingClientRect();
    const agora = cena(vistaRef.current);
    const { ox, oy, k } = vistaDe(caixa.width, caixa.height, agora, false);
    const sx = e.clientX - caixa.left;
    const sy = e.clientY - caixa.top;
    return { agora, k, x: ox + (sx - caixa.width / 2) / k, y: oy + (sy - caixa.height / 2) / k };
  };

  /** A pessoa ou o ponto de interesse debaixo do rato, se houver. */
  const debaixoDoRato = (agora: Cena, k: number, x: number, y: number) => {
    const pessoa = agora.pessoas.find((p) => Math.hypot((p.x - x) * k, (p.y - y) * k) <= 10);
    const ponto = pessoa
      ? undefined
      : PONTOS_DE_INTERESSE.find((p) => bandaDe(p.y) === agora.banda && Math.hypot((p.x - x) * k, (p.y - y) * k) <= 12);
    return { pessoa, ponto };
  };

  const carregarNoMapa = (e: MouseEvent<HTMLCanvasElement>) => {
    const { agora, k, x, y } = noMundo(e);
    const { pessoa, ponto } = debaixoDoRato(agora, k, x, y);
    // Uma pessoa perto do clique ganha ao chão: vai-se ter com ela.
    if (pessoa) {
      destinoRef.current = null;
      onIrTerCom(pessoa.id);
      setAviso(`A caminho de ${pessoa.nome}`);
      return;
    }
    const { y0, y1 } = limitesDaBanda(agora.banda);
    const alvoX = ponto ? ponto.x : x;
    const alvoY = ponto ? ponto.y : y;
    if (alvoY < y0 || alvoY >= y1) return;
    const alvo = destinoMaisProximo(alvoX, alvoY);
    if (!alvo || !onIrPara(alvo.x, alvo.y)) {
      const bunker = agora.banda !== agora.bandaDoEu && (agora.banda === "subsolo" || agora.bandaDoEu === "subsolo");
      setAviso(bunker ? "Ao bunker vai-se pela escotilha do jardim." : "Não há caminho até aí.");
      return;
    }
    destinoRef.current = alvo;
    setAviso(`A caminho: ${ponto ? ponto.nome : placeName(alvo.x, alvo.y)}`);
  };

  const passarNoMapa = (e: MouseEvent<HTMLCanvasElement>) => {
    const { agora, k, x, y } = noMundo(e);
    const { y0, y1 } = limitesDaBanda(agora.banda);
    if (x < 0 || x >= MAP_WIDTH || y < y0 || y >= y1) {
      setSobre(null);
      return;
    }
    const { pessoa, ponto } = debaixoDoRato(agora, k, x, y);
    if (pessoa) setSobre(`${pessoa.nome}${pessoa.emChamada ? " · em chamada" : ""} · ${placeName(pessoa.x, pessoa.y)}`);
    else if (ponto) setSobre(`${ponto.nome} · ${placeName(ponto.x, ponto.y)}`);
    else setSobre(placeName(x, y));
  };

  const bandaVista = vista ?? banda;

  return (
    <>
      <div className="minimapa">
        <button
          type="button"
          className="minimapa__disco"
          onClick={() => onAbrir(true)}
          title="Abrir o mapa (M)"
          aria-label="Abrir o mapa"
        >
          <canvas ref={discoRef} className="minimapa__tela" />
          <span className="minimapa__norte">N</span>
          <span className="minimapa__rumo minimapa__rumo--l">L</span>
          <span className="minimapa__rumo minimapa__rumo--s">S</span>
          <span className="minimapa__rumo minimapa__rumo--o">O</span>
        </button>
        <div className="minimapa__info">
          <span className="minimapa__lugar">
            <span>
              {banda === "subsolo" ? "Bunker · " : banda === "andar1" && lugar !== "1.º andar" ? "1.º andar · " : ""}
              {lugar}
            </span>
            {companhia > 0 ? (
              <span className="minimapa__companhia">
                {companhia === 1 ? " · +1 pessoa" : ` · +${companhia} pessoas`}
              </span>
            ) : null}
          </span>
          <span
            className="minimapa__hora"
            title={escolhido ? `${NOME_DO_TEMPO[tempo]} (escolhido com /clima)` : NOME_DO_TEMPO[tempo]}
          >
            {SIMBOLO_DO_TEMPO[tempo] ? `${SIMBOLO_DO_TEMPO[tempo]} ` : ""}
            {hora}
          </span>
        </div>
      </div>

      {aberto ? (
        <div className="minimapa-grande" role="dialog" aria-modal="true" aria-label="Mapa do escritório">
          <div className="minimapa-grande__janela">
            <header className="minimapa-grande__cab">
              <h2>Mapa · {NOME_DA_BANDA[bandaVista]}</h2>
              <fieldset className="minimapa-grande__andares" aria-label="Andar do mapa">
                {ANDARES.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={`minimapa-grande__andar${b === bandaVista ? " is-ativo" : ""}`}
                    aria-pressed={b === bandaVista}
                    onClick={() => setVista(b === banda ? null : b)}
                  >
                    {NOME_DA_BANDA[b]}
                    {b === banda ? <i className="minimapa-grande__aqui" title="Você está aqui" /> : null}
                  </button>
                ))}
              </fieldset>
              <span className="minimapa-grande__dica">
                Clique num lugar, numa pessoa ou num ícone para ir até lá · <kbd>M</kbd> fecha
              </span>
              <button type="button" className="icon-button" onClick={() => onAbrir(false)} aria-label="Fechar o mapa">
                <IconClose size={18} />
              </button>
            </header>
            <canvas
              ref={grandeRef}
              className="minimapa-grande__tela"
              onClick={carregarNoMapa}
              onMouseMove={passarNoMapa}
              onMouseLeave={() => setSobre(null)}
            />
            <footer className="minimapa-grande__rodape">
              <span className="minimapa-grande__legenda">
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--eu" />
                  Você
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--pessoa" />
                  Pessoas
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--chamada" />
                  Em chamada
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--destino" />
                  Destino
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--ponto" />
                  Bater ponto
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--quadro" />
                  Quadro
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--jogo" />
                  Jogos
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--tv" />
                  TV
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--escotilha" />
                  Escotilha
                </span>
                <span>
                  <i className="minimapa-grande__marca minimapa-grande__marca--trancada" />
                  Sala trancada
                </span>
              </span>
              <span className="minimapa-grande__estado" aria-live="polite">
                {aviso ?? sobre ?? ""}
              </span>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
